/**
 * DublettenTop20Page — Report „Top-20-Dubletten-Kandidaten" (Issue #384).
 *
 * Konsumiert die View `crm_kontakt_dubletten_admin` (#376) und zeigt die
 * 20 Paare mit höchstem Score als kompakte Tabelle. Spalten: Kontakt-A,
 * Kontakt-B, Match-Grund, Score, Erkannt am, Link zur Merge-Wizard-Detail-
 * Ansicht (#383). Filter nach `match_reason` (alle / email / phone / fuzzy).
 * CSV-Export der aktuell sichtbaren Zeilen.
 *
 * Admin-Zugriff: doppelt abgesichert — der DB-View filtert per RLS-Substitute
 * auf admin-Rolle, und die Route ist via ModuleGuard moduleId='crm' im
 * Router geschützt.
 */

import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { AlertTriangle, BarChart3, Download, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCRMDubletten } from '@/hooks/useCRMDubletten';
import {
  downloadDublettenCsv,
  filterByMatchReason,
  matchReasonLabel,
  type MatchReasonFilter,
} from '@/lib/dubletten-report';

const FILTER_OPTIONS: ReadonlyArray<{ value: MatchReasonFilter; label: string }> = [
  { value: 'alle', label: 'Alle Match-Gründe' },
  { value: 'email', label: 'Nur E-Mail' },
  { value: 'phone', label: 'Nur Telefon' },
  { value: 'fuzzy', label: 'Nur Unscharf' },
];

function ScoreBadge({ score }: { score: number }) {
  const tone =
    score >= 100
      ? 'bg-red-50 text-red-700 border-red-200'
      : score >= 90
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-blue-50 text-blue-700 border-blue-200';
  return (
    <span
      className={`inline-flex min-w-[2.25rem] justify-center rounded border px-1.5 py-0.5 text-xs font-semibold ${tone}`}
      data-testid="dubletten-score"
    >
      {score}
    </span>
  );
}

function ReasonBadge({ reason }: { reason: 'email' | 'phone' | 'fuzzy' }) {
  const tone =
    reason === 'email'
      ? 'bg-red-50 text-red-700 border-red-200'
      : reason === 'phone'
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-blue-50 text-blue-700 border-blue-200';
  return (
    <Badge variant="outline" className={`${tone} font-medium`}>
      {matchReasonLabel(reason)}
    </Badge>
  );
}

function formatErkanntAm(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function DublettenTop20Page() {
  const { rows, loading, error, refetch } = useCRMDubletten();
  const [filter, setFilter] = useState<MatchReasonFilter>('alle');

  const filteredRows = useMemo(() => {
    return filterByMatchReason(
      rows.map((r) => ({
        kontakt_a_id: r.kontakt_a_id,
        kontakt_b_id: r.kontakt_b_id,
        match_reason: r.match_reason,
        score: r.score,
        erkannt_am: r.erkannt_am,
      })),
      filter,
    ).map((k) => rows.find((r) => r.kontakt_a_id === k.kontakt_a_id && r.kontakt_b_id === k.kontakt_b_id)!);
  }, [rows, filter]);

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <BarChart3 className="h-6 w-6 text-[#B91C1C]" />
            Top-20 Dubletten-Kandidaten
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Die 20 Kontakt-Paare mit dem höchsten Match-Score. Sortiert nach Score absteigend —
            zur Priorisierung der Bereinigung.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="match-reason-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as MatchReasonFilter)}
            className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm"
          >
            {FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={loading}
            data-testid="dubletten-refresh"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadDublettenCsv(filteredRows)}
            disabled={filteredRows.length === 0}
            data-testid="dubletten-export-csv"
          >
            <Download className="mr-2 h-4 w-4" />
            CSV-Export
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <div className="font-medium">Konnte Dubletten nicht laden</div>
            <div className="text-red-700">{error}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-10 text-center text-sm text-gray-500">
          Keine Dubletten-Kandidaten gefunden. Der nightly Refresh läuft täglich um 03:00 Uhr.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Kontakt A</TableHead>
                <TableHead>Kontakt B</TableHead>
                <TableHead className="w-24">Match</TableHead>
                <TableHead className="w-20">Score</TableHead>
                <TableHead className="w-28">Erkannt am</TableHead>
                <TableHead className="w-24 text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map((row, idx) => {
                const mergeHref = `/crm/dubletten?a=${encodeURIComponent(row.kontakt_a_id)}&b=${encodeURIComponent(row.kontakt_b_id)}`;
                return (
                  <TableRow
                    key={`${row.kontakt_a_id}-${row.kontakt_b_id}`}
                    data-testid="dubletten-row"
                  >
                    <TableCell className="text-xs font-medium text-gray-500">{idx + 1}</TableCell>
                    <TableCell>
                      <div className="font-medium text-gray-900">{row.kontakt_a_name}</div>
                      {row.kontakt_a_firma && row.kontakt_a_firma !== row.kontakt_a_name && (
                        <div className="text-xs text-gray-500">{row.kontakt_a_firma}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-gray-900">{row.kontakt_b_name}</div>
                      {row.kontakt_b_firma && row.kontakt_b_firma !== row.kontakt_b_name && (
                        <div className="text-xs text-gray-500">{row.kontakt_b_firma}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <ReasonBadge reason={row.match_reason} />
                    </TableCell>
                    <TableCell>
                      <ScoreBadge score={row.score} />
                    </TableCell>
                    <TableCell className="text-xs text-gray-600">
                      {formatErkanntAm(row.erkannt_am)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        href={mergeHref}
                        data-testid="dubletten-merge-link"
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Prüfen
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
